import { NgClass, NgFor, NgIf, NgStyle, NgSwitch, NgSwitchCase, NgSwitchDefault, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, Input, forwardRef, inject } from '@angular/core';
import { NavigationStart, Router, RouterLink } from '@angular/router';
import { DfDomModule } from '@df/dom/df-dom.module';
import { Ga4TrackClickDirective } from '@df/module-gtm/ga4-track-click.directive';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { DfImageWrapperComponent } from '@df/module-image/df-image-wrapper.component';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ProductComponent } from '../../catalog/ui/product/product.component';
import { MEGANAV_GROUP_ID } from '../page/header/page-header.component';
import { IColorList, IColumnsData, IMenuItem, ISubmenuData } from '../page/header/page-header.interface';
import { PageHeaderMaganavContentAreaDirective } from './area/page-header-meganav-content-area.directive';
/**
 * Meganav content.
 *
 * @usage
 * ```html
 * <meganav-content></meganav-content>
 * ```
 */
@Component({
    selector: 'meganav-content',
    templateUrl: 'meganav-content.html',
    standalone: true,
    imports: [
        DfDomModule,
        forwardRef(() => ProductComponent),
        Ga4TrackClickDirective,
        GridModule,
        HrefComponent,
        ICmsClientModule,
        DfImageWrapperComponent,
        DfImageDirective,
        NgClass,
        NgStyle,
        NgSwitch,
        NgSwitchCase,
        NgSwitchDefault,
        NgTemplateOutlet,
        PageHeaderMaganavContentAreaDirective,
        RouterLink,
        ToggleModule,
        TranslationModule
    ]
})
export class MeganavContentComponent extends UiComponent {
    currentOpen = 0;
    currentColor = 0;

    @Input()
    items!: IMenuItem[];

    private toggleService = inject(ToggleService);
    private ref = inject(ChangeDetectorRef);
    private router = inject(Router);

    readonly meganavToggleGroupId = MEGANAV_GROUP_ID;

    override ngOnInit(): void {
        this.subscriptions.add(
            this.router.events.subscribe(event => {
                if (event instanceof NavigationStart) {
                    this.toggleService.closeGroup(this.meganavToggleGroupId);
                }
            })
        );
        super.ngOnInit();
    }

    showTitleOrSpace(column: IColumnsData): boolean | string | undefined {
        return (column.space_above || column.title) && column.variant !== 'colors';
    }
    openTab(item: ISubmenuData, index: number): void {
        if (item.columns.length && this.currentOpen != index) {
            this.currentOpen = index;
            this.ref.detectChanges();
        }
    }
    closeTab() {
        if (this.currentOpen) {
            this.currentOpen = 0;
            this.ref.detectChanges();
        }
    }
    showColorImage(item: IColorList, index: number): void {
        if (item.images.length && this.currentColor !== index) {
            this.currentColor = index;
            this.ref.detectChanges();
        }
    }
    hasColumnsToShow(columns: IColumnsData[]): boolean {
        return !!columns.find(column => this.canShowColumn(column));
    }
    canShowColumn(column: IColumnsData): boolean {
        return column.variant !== 'image' && column.variant !== 'products';
    }
    hasImageProductColumnsToShow(columns: IColumnsData[]): boolean {
        return !!columns.find(column => this.canShowImageProductColumn(column));
    }
    canShowImageProductColumn(column: IColumnsData): boolean {
        return column.variant === 'image' || column.variant === 'products';
    }
    closeOpenColor() {
        if (this.currentColor) {
            this.currentColor = 0;
            this.ref.detectChanges();
        }
    }
    // Original with of images base on vw but when we have bigger screen then max width in wrap we should use rem
    imageWidth(originalWidth: number): string {
        return this.client.width > 1680 ? ((16.8 * originalWidth) / 16).toString() + 'rem' : originalWidth.toString() + 'vw';
    }
    protected getNavigationGA4Data(navParent: string, navLvl?: string, navItem?: string): Gtm.IGA4TrackData {
        return {
            name: 'navigation_engagement',
            details: { nav_parent: navParent, nav_lvl2: navLvl, nav_item: navItem }
        };
    }

    get isCms(): boolean {
        return this.client.isCms && !this.client.isCmsPreview;
    }

    handleMouseEnter(index: number): void {
        this.toggleService.open([this.meganavToggleGroupId, index]);
    }

    handleMouseLeave(index: number): void {
        if (!this.isCms) {
            this.toggleService.close([this.meganavToggleGroupId, index]);
        }
    }
}
